import React from 'react';
import {
  Calendar,
  Clock,
  DollarSign,
  Hash,
  Home,
  Landmark,
  Loader2,
  ShieldCheck,
  User,
} from 'lucide-react';
import { useParams } from 'react-router-dom';

import { useGetCounterProposalQuery } from '@/apis/extended/negotiation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ContractClauseStatus, ContractStatus } from '@/types/StatusType';
import { ClauseStatusBadge, NegotiationStatusBadge } from './statusbadge';

const FieldRow = ({
  icon: Icon,
  label,
  value,
  status,
}: {
  icon: React.ElementType;
  label: string;
  value?: string;
  status?: string;
}) => {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md bg-[var(--surface-container-lowest)] p-3">
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-1 text-gray-300" />
        <div>
          <div className="text-sm font-medium text-white">{label}</div>
          <div className="text-sm text-gray-300 whitespace-pre-wrap break-words">
            {value ?? '—'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {status && ClauseStatusBadge(status as ContractClauseStatus)}
      </div>
    </div>
  );
};

const CounterPropose = () => {
  const { contractId } = useParams();
  const { data, isLoading, isError, refetch } = useGetCounterProposalQuery(
    contractId ?? '',
    {
      skip: !contractId,
      refetchOnMountOrArgChange: true,
    }
  );

  const hasValue = (field: { value: string }): boolean => {
    if (field === null || field === undefined) return false;
    if (typeof field === 'object') {
      const value = field?.value ?? '';
      if (value === null || value === undefined) return false;
      return String(value).trim().length > 0;
    }
    return String(field).trim().length > 0;
  };

  const negotiation = data?.negotiation;
  return (
    <div className="min-h-screen bg-[var(--surface)] text-[var(--on-surface)]">
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-6">Counter Proposed</h1>

        <Card className="mb-6 bg-[var(--surface-container-lowest)] shadow-none">
          <CardHeader>
            <CardTitle className="text-center text-white">
              Proposed Negotiation Clauses
            </CardTitle>
          </CardHeader>
          <div className="flex justify-center items-center mb-4">
            <span className="capitalize">
              {NegotiationStatusBadge(
                (typeof negotiation?.NegotiationStatus === 'object'
                  ? negotiation?.NegotiationStatus?.value
                  : negotiation?.NegotiationStatus) as ContractStatus
              )}
            </span>
          </div>
          <CardContent>
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-[#043a93]" />
                  <span className="text-gray-300 font-medium">
                    Loading proposal details...
                  </span>
                </div>
              </div>
            )}
            {isError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <Hash className="w-5 h-5 text-red-300" />
                  </div>
                  <span className="text-red-300 font-medium">
                    Failed to load counter proposal
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refetch}
                  className="bg-red-500/20 border-red-500/30 text-red-300 hover:bg-red-500/30 hover:border-red-500/40"
                >
                  <Loader2 className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            )}

            {!isLoading && negotiation && (
              <div className="space-y-8">
                {/* Metadata Section */}
                <div className="bg-[#043a93]/10 border border-[#043a93]/20 rounded-xl p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {negotiation.proposedBy && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-300" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Proposed By
                          </span>
                          <span className="text-white font-medium capitalize">
                            {typeof negotiation.proposedBy === 'object'
                              ? negotiation.proposedBy?.value
                              : negotiation.proposedBy}
                          </span>
                        </div>
                      </div>
                    )}
                    {negotiation.timestamp && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-gray-300" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Updated At
                          </span>
                          <span className="text-white font-medium">
                            {new Date(negotiation.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                    {negotiation._id && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <Hash className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Negotiation ID
                          </span>
                          <span className="text-white font-mono text-sm">
                            {negotiation._id}
                          </span>
                        </div>
                      </div>
                    )}
                    {negotiation.contractId && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#043a93]/20 rounded-lg flex items-center justify-center">
                          <Hash className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <span className="block text-sm text-gray-400">
                            Contract ID
                          </span>
                          <span className="text-white font-mono text-sm">
                            {negotiation.contractId}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Terms Section */}
                {(hasValue(negotiation.offeredPrice) ||
                  hasValue(negotiation.EarnestMoney) ||
                  hasValue(negotiation.ClosingDate)) && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#043a93]" />
                      Financial Terms
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {hasValue(negotiation.offeredPrice) && (
                        <FieldRow
                          icon={DollarSign}
                          label="Offered Price"
                          value={
                            typeof negotiation.offeredPrice === 'object'
                              ? negotiation.offeredPrice?.value
                              : negotiation.offeredPrice
                          }
                          status={
                            typeof negotiation.offeredPrice === 'object'
                              ? negotiation.offeredPrice?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(negotiation.EarnestMoney) && (
                        <FieldRow
                          status={negotiation.EarnestMoney?.status}
                          icon={Landmark}
                          label="Earnest Money"
                          value={
                            typeof negotiation.EarnestMoney === 'object'
                              ? negotiation.EarnestMoney?.value
                              : negotiation.EarnestMoney
                          }
                        />
                      )}
                      {hasValue(negotiation.ClosingDate) && (
                        <FieldRow
                          icon={Calendar}
                          label="Closing Date"
                          value={
                            typeof negotiation.ClosingDate === 'object'
                              ? negotiation.ClosingDate?.value
                              : negotiation.ClosingDate
                          }
                          status={
                            typeof negotiation.ClosingDate === 'object'
                              ? negotiation.ClosingDate?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Contingencies Section */}
                {(hasValue(negotiation.AppraisalContingency) ||
                  hasValue(negotiation.FinancingContingency) ||
                  hasValue(negotiation.HomeInspection) ||
                  hasValue(negotiation.postCloseOccupancy) ||
                  hasValue(negotiation.Suggested) ||
                  hasValue(negotiation.Contingent)) && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-[#043a93]" />
                      Contingencies & Additional Terms
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {hasValue(negotiation.AppraisalContingency) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Appraisal Contingency"
                          value={
                            typeof negotiation.AppraisalContingency === 'object'
                              ? negotiation.AppraisalContingency?.value
                              : negotiation.AppraisalContingency
                          }
                          status={
                            typeof negotiation.AppraisalContingency === 'object'
                              ? negotiation.AppraisalContingency
                                  ?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(negotiation.FinancingContingency) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Financing Contingency"
                          value={
                            typeof negotiation.FinancingContingency === 'object'
                              ? negotiation.FinancingContingency?.value
                              : negotiation.FinancingContingency
                          }
                          status={
                            typeof negotiation.FinancingContingency === 'object'
                              ? negotiation.FinancingContingency
                                  ?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(negotiation.HomeInspection) && (
                        <FieldRow
                          icon={Home}
                          label="Home Inspection"
                          value={
                            typeof negotiation.HomeInspection === 'object'
                              ? negotiation.HomeInspection?.value
                              : negotiation.HomeInspection
                          }
                          status={
                            typeof negotiation.HomeInspection === 'object'
                              ? negotiation.HomeInspection?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(negotiation.postCloseOccupancy) && (
                        <FieldRow
                          icon={Home}
                          label="Post Close Occupancy"
                          value={
                            typeof negotiation.postCloseOccupancy === 'object'
                              ? negotiation.postCloseOccupancy?.value
                              : negotiation.postCloseOccupancy
                          }
                          status={
                            typeof negotiation.postCloseOccupancy === 'object'
                              ? negotiation.postCloseOccupancy
                                  ?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(negotiation.Suggested) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Suggested"
                          value={
                            typeof negotiation.Suggested === 'object'
                              ? negotiation.Suggested?.value
                              : negotiation.Suggested
                          }
                          status={
                            typeof negotiation.Suggested === 'object'
                              ? negotiation.Suggested?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                      {hasValue(negotiation.Contingent) && (
                        <FieldRow
                          icon={ShieldCheck}
                          label="Contingent"
                          value={
                            typeof negotiation.Contingent === 'object'
                              ? negotiation.Contingent?.value
                              : negotiation.Contingent
                          }
                          status={
                            typeof negotiation.Contingent === 'object'
                              ? negotiation.Contingent?.subsetClauseStatus
                              : undefined
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isLoading && !negotiation && (
              <div className="text-sm text-gray-400">No negotiation found.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CounterPropose;
